import { useEffect, useMemo } from 'react';

import { ALL_OPTION_KEY } from '@/common/constants';
import { API_URL, IReportFilterDtoDelinquentClaimsViewTypeEnum } from '@/dtos';
import { PLAN_YEAR_OPTIONS } from '@/modules/Reports/report.types';

import { useGetEmployerIdParam } from '../../hooks';
import { useBaseReportTableQuery } from '../../queries';
import {
  DelinquentClaimsReportTableParams,
  DelinquentClaimsReportView,
} from '../DelinquentClaimsReport.types';

export const QUERY_KEY = 'DELINQUENT_CLAIMS_REPORT_QUERY';

const MIN_SEARCH_LEN = 3;

const useDelinquentClaimsReportQuery = (params: Partial<DelinquentClaimsReportTableParams>) => {
  const currentSearch = useMemo(() => {
    if (params?.searchString && params.searchString.length >= MIN_SEARCH_LEN) {
      return params.searchString;
    }
    return null;
  }, [params?.searchString]);

  const getEmployerIdParam = useGetEmployerIdParam();
  const employerIdsString = useMemo(() => params?.employerIds?.join(','), [params?.employerIds]);
  const reportView = params.reportView || DelinquentClaimsReportView.ByEmployee;

  const base = useBaseReportTableQuery(
    API_URL.REPORTS_DELINQUENT_CLAIMS_REPORT_BREAKDOWN,
    [
      QUERY_KEY,
      reportView,
      params.employerId,
      params.accountTypes?.join(','),
      params.planIds?.join(','),
      params.planYearPeriod?.join(','),
      params.claimStatuses?.join(','),
      currentSearch,
      employerIdsString,
    ],
    {
      ...getEmployerIdParam(params.employerId, params.employerIds),
      show_ssn: true,
      isSSNShowed: params.isSSNShowed,
      search_value: currentSearch,
      delinquent_claims_view_type:
        reportView === DelinquentClaimsReportView.ByClaim
          ? IReportFilterDtoDelinquentClaimsViewTypeEnum.BY_CLAIM
          : IReportFilterDtoDelinquentClaimsViewTypeEnum.BY_EMPLOYEE,
      account_types:
        params.accountTypes && !params.accountTypes.includes(ALL_OPTION_KEY)
          ? params.accountTypes.join(',')
          : undefined,
      plan_ids:
        params.isMultipleEmployer && params.employerIds?.length !== 1
          ? undefined
          : params.planIds && !params.planIds.includes(ALL_OPTION_KEY)
            ? params.planIds.join(',')
            : undefined,
      plan_year_periods:
        params.isMultipleEmployer && params.employerIds?.length !== 1
          ? PLAN_YEAR_OPTIONS[2].key
          : params.planYearPeriod && !params.planYearPeriod.includes(ALL_OPTION_KEY)
            ? params.planYearPeriod.join(',')
            : undefined,
      claim_statuses:
        params.reportView !== DelinquentClaimsReportView.ByClaim
          ? undefined
          : params.claimStatuses && !params.claimStatuses.includes(ALL_OPTION_KEY)
            ? params.claimStatuses.join(',')
            : undefined,
    },
    {
      isMultipleEmployers: params.isMultipleEmployer,
      isPreviewMode: params.isPreviewMode,
    }
  );

  useEffect(() => {
    base.onChangePlanOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.employerId, employerIdsString]);

  useEffect(() => {
    base.setPage(0);
    base.setRawContentList([]);
    base.setRawHeaders([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.employerId,
    employerIdsString,
    params.accountTypes,
    params.planIds,
    params.planYearPeriod,
    params.claimStatuses,
    params.searchString,
    reportView,
  ]);

  return base;
};

export default useDelinquentClaimsReportQuery;
